'use client';

import { useState } from 'react';
import { Bot, X, Send, Loader2, ThumbsUp, ThumbsDown, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { knowledgeBaseApi, type AskResponse, type SourceDocument } from '../_lib/api';

interface KBChatbotButtonProps {
  className?: string;
}

type AuthorityLevelKey = 'OFFICIAL' | 'EXPERT' | 'PUBLISHED' | 'DRAFT';

export function KBChatbotButton({ className = '' }: KBChatbotButtonProps) {
  const { t, locale } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [lastQuestion, setLastQuestion] = useState(''); // 保存发送的问题用于显示
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AskResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAsk = async () => {
    if (!question.trim() || loading) return;

    const currentQuestion = question.trim();
    setLastQuestion(currentQuestion); // 保存问题用于显示
    setQuestion(''); // 清空输入框

    try {
      setLoading(true);
      setError(null);
      // apiClient 响应拦截器已自动解包，result 直接是 AskResponse
      const result = await knowledgeBaseApi.ask(currentQuestion);
      setResponse(result);
    } catch (err) {
      console.error('Failed to ask:', err);
      setError(t.knowledgeBase.ask.askFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (feedback: 'THUMBS_UP' | 'THUMBS_DOWN') => {
    if (!response) return;
    try {
      await knowledgeBaseApi.submitFeedback(response.id, feedback);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-blue-600 text-white shadow-lg transition-all hover:scale-105 hover:shadow-xl',
          className,
        )}
        aria-label={t.knowledgeBase.chatbot.ariaLabel}
      >
        <Bot className="h-6 w-6" />
      </button>

      {/* Chat dialog */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex h-[500px] w-[380px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between bg-gradient-to-r from-purple-500 to-blue-600 px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <span className="font-medium">{t.knowledgeBase.chatbot.title}</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="rounded-full p-1 hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {!response && !loading && !error && (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className="mb-4 rounded-full bg-purple-100 p-4">
                  <Bot className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">
                  {t.knowledgeBase.chatbot.emptyTitle}
                </h3>
                <p className="text-sm text-gray-500">
                  {t.knowledgeBase.chatbot.emptyDescription}
                </p>
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">{error}</div>
            )}

            {response && !loading && (
              <div className="space-y-4">
                {/* Question */}
                <div className="flex justify-end">
                  <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-purple-600 px-4 py-2 text-sm text-white">
                    {lastQuestion}
                  </div>
                </div>

                {/* Answer */}
                <div className="rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3">
                  <p className="whitespace-pre-wrap text-sm text-gray-800">{response.answer}</p>

                  {/* Feedback */}
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-gray-400">{t.knowledgeBase.ask.helpful}</span>
                    <span className="text-xs text-gray-400">{t.knowledgeBase.ask.helpful}</span>
                    <button
                      onClick={() => handleFeedback('THUMBS_UP')}
                      className="rounded p-1 hover:bg-green-100"
                    >
                      <ThumbsUp className="h-4 w-4 text-gray-400 hover:text-green-600" />
                    </button>
                    <button
                      onClick={() => handleFeedback('THUMBS_DOWN')}
                      className="rounded p-1 hover:bg-red-100"
                    >
                      <ThumbsDown className="h-4 w-4 text-gray-400 hover:text-red-600" />
                    </button>
                  </div>
                </div>

                {/* Sources */}
    {response.sources.length > 0 && (
      <div className="space-y-2">
        <p className="text-xs font-medium text-gray-500">{t.knowledgeBase.ask.sources}:</p>
        {response.sources.slice(0, 3).map((source, i) => (
          <SourceCard
            key={source.id}
            source={source}
            index={i + 1}
            scoreLabel={t.knowledgeBase.chatbot.scoreLabel}
            formatScore={(value) =>
              new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)
            }
            authorityLabel={t.knowledgeBase.authorityLevel}
          />
        ))}
      </div>
    )}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-100 p-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t.knowledgeBase.chatbot.inputPlaceholder}
                className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm outline-none focus:border-purple-300 focus:ring-2 focus:ring-purple-100"
                disabled={loading}
              />
              <button
                onClick={handleAsk}
                disabled={!question.trim() || loading}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-600 text-white transition-colors hover:bg-purple-700 disabled:bg-gray-300"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SourceCard({
  source,
  index,
  scoreLabel,
  formatScore,
  authorityLabel,
}: {
  source: SourceDocument;
  index: number;
  scoreLabel: string;
  formatScore: (value: number) => string;
  authorityLabel: Record<string, string>;
}) {
  const authorityColors: Record<string, string> = {
    OFFICIAL: 'bg-green-100 text-green-700',
    EXPERT: 'bg-blue-100 text-blue-700',
    PUBLISHED: 'bg-gray-100 text-gray-700',
    DRAFT: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 p-2">
      <div className="flex items-start gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-100 text-xs font-medium text-purple-600">
          {index}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-xs font-medium text-gray-800">{source.title}</p>
            {source.webUrl && (
              <a
                href={source.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span
              className={cn(
                'rounded px-1.5 py-0.5 text-[10px] font-medium',
                authorityColors[source.authorityLevel] || 'bg-gray-100 text-gray-600',
              )}
            >
              {authorityLabel[source.authorityLevel] || source.authorityLevel}
            </span>
            <span className="text-[10px] text-gray-400">
              {scoreLabel.replace('{score}', formatScore(source.relevanceScore * 100))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
